/**
 * Edit Invites Modal
 * Modal for updating user's invite count
 */
import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Text,
  VStack,
  HStack,
} from '@chakra-ui/react';
import { adminService, type AdminUser } from '../../services/AdminService';
import { useAdminModalHandler } from '../../hooks/useAdminModalHandler';

interface EditInvitesModalProps {
  user: AdminUser;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const EditInvitesModal: React.FC<EditInvitesModalProps> = ({
  user,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [inviteCount, setInviteCount] = useState(user.invites_available);
  const { loading, handleSubmit } = useAdminModalHandler({ onSuccess, onClose });

  // Update invite count when user changes
  useEffect(() => {
    setInviteCount(user.invites_available);
  }, [user]);

  const onSubmit = () => handleSubmit(
    () => adminService.updateUserInvites(user.id, inviteCount),
    `Updated invite count for ${user.username}`,
    'Failed to update invites'
  );

  // Admin check
  if (user.role === 'admin') {
    return (
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit Invites</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Text>
                <strong>{user.username}</strong> is an admin and has unlimited invites.
              </Text>
              <Text fontSize="sm" color="gray.600">
                Admins automatically have unlimited invite generation capability.
              </Text>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Edit Invites</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            {/* User Info */}
            <VStack align="stretch" spacing={1}>
              <Text fontSize="sm" color="gray.600">
                User
              </Text>
              <Text fontWeight="medium">{user.username}</Text>
              <Text fontSize="sm" color="gray.500">
                {user.email}
              </Text>
            </VStack>

            {/* Current Invites */}
            <VStack align="stretch" spacing={1}>
              <Text fontSize="sm" color="gray.600">
                Current Invites
              </Text>
              <Text fontSize="2xl" fontWeight="bold">
                {user.invites_available}
              </Text>
            </VStack>

            {/* New Invite Count */}
            <FormControl>
              <FormLabel>New Invite Count</FormLabel>
              <NumberInput
                value={inviteCount}
                onChange={(_, value) => setInviteCount(value)}
                min={0}
                max={10000}
              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            </FormControl>

            {/* Change Preview */}
            <HStack
              p={3}
              bg="blue.50"
              borderRadius="md"
              justify="space-between"
            >
              <Text fontSize="sm" color="blue.700">
                Change:
              </Text>
              <Text fontSize="sm" fontWeight="bold" color="blue.700">
                {inviteCount - user.invites_available > 0 ? '+' : ''}
                {inviteCount - user.invites_available}
              </Text>
            </HStack>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={onSubmit}
            isLoading={loading}
            isDisabled={inviteCount === user.invites_available}
          >
            Update
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default EditInvitesModal;
